// client/src/dnd/views/Interactive_parts/TokenLayer.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Marker, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import ClickHandler from './ClickHandler.js';

function playerTokenIcon(colour = "#3b82f6", size = 36) {
  return L.divIcon({
    className: 'player-token-icon',
    html: `<div style="
      width:${size}px;
      height:${size}px;
      border-radius:50%;
      background:${colour};
      border:2.5px solid #23242a;
      box-shadow:0 2px 8px #0008;
      pointer-events:auto;
      cursor:grab;
    "></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function lightTokenIcon(colour = "#ffe072", iconUrl = null, size = 38) {
  if (iconUrl) {
    return L.divIcon({
      className: 'light-token-icon',
      html: `<div style="width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center;">
        <img src="${iconUrl}" style="width:${size}px;height:${size}px;border-radius:50%;object-fit:cover;border:2.5px solid #ffe072;box-shadow:0 2px 24px 0 ${colour}88;" />
      </div>`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
  }
  return L.divIcon({
    className: 'light-token-icon',
    html: `<div style="
      width:${size}px;
      height:${size}px;
      border-radius:50%;
      background:radial-gradient(ellipse at center, ${colour} 65%, #b2952f 100%);
      border:2.5px solid ${colour};
      box-shadow:0 2px 24px 0 ${colour}88;
      display:flex;align-items:center;justify-content:center;
      font-size:22px;
    "><span style="filter: drop-shadow(0 0 2px #fff);">💡</span></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function isImageFile(url) {
  return typeof url === "string" && (url.endsWith('.png') || url.endsWith('.jpg') || url.endsWith('.jpeg') || url.endsWith('.gif'));
}

function isVideoFile(url) {
  return typeof url === "string" && (url.endsWith('.mp4') || url.endsWith('.webm'));
}

const TOKENS_API_URL = "/api/tabletop/tokens";

function imgPxToScreenPx(map, imgPx) {
  const p0 = map.latLngToContainerPoint([0, 0]);
  const pX = map.latLngToContainerPoint([0, imgPx]);
  const pY = map.latLngToContainerPoint([imgPx, 0]);
  const sx = Math.abs(pX.x - p0.x);
  const sy = Math.abs(pY.y - p0.y);
  return (sx + sy) / 2;
}

function TokenMarker({
  token,
  sizePx,
  isLight,
  onDragEnd,
  onClick,
}) {
  const markerRef = useRef(null);
  const icon = isLight
    ? lightTokenIcon(token.colour, token.icon_url, sizePx)
    : playerTokenIcon(token.colour, sizePx);

  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.setIcon(icon);
    }
  }, [icon]);

  return (
    <Marker
      ref={markerRef}
      key={token.id}
      position={[token.y, token.x]}
      icon={icon}
      draggable={true}
      eventHandlers={{
        dragend: onDragEnd,
        click: onClick
      }}
    >
      <Tooltip direction="top" offset={[0, -sizePx / 1.5]} opacity={1} permanent={false}>
        {isLight ? (token.label || "Light Source") : token.label}
      </Tooltip>
    </Marker>
  );
}

export default function TokenLayer({
  imageSize,
  addTokenMode,
  onFinishAddToken,
  tokens,
  setTokens,
  user,
  isMapOwnerOrFounder,
  mapId,
  onSightLightMarkersChange,
  gridConfig,
  openPlayerTokenId,
  onPlayerTokenModalHandled,
}) {
  const map = useMap();

  const [addTypeModal, setAddTypeModal] = useState(false);
  const [pendingAddType, setPendingAddType] = useState(null);
  const [addTokenReady, setAddTokenReady] = useState(false);
  const [modalTokenIdx, setModalTokenIdx] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [uploadingIcon, setUploadingIcon] = useState(false);

  const reloadTokens = useCallback(() => {
    if (!mapId) return;
    fetch(`${TOKENS_API_URL}/${mapId}`)
      .then(res => res.json())
      .then(data => {
        const fixedTokens = Array.isArray(data) ? data.map(t => {
          return {
            ...t,
            emits_light: Boolean(t.emits_light),
            visible_to_all: Boolean(t.visible_to_all),
            x: Number(t.x),
            y: Number(t.y),
            reach: Number(t.reach) || 350,
            intensity: Number(t.intensity) || 1.0,
            controlled_user_id: t.controlled_user_id != null ? Number(t.controlled_user_id) : null,
          };
        }) : [];
        setTokens(fixedTokens);
      })
      .catch(() => {
        setTokens([]);
      });
  }, [mapId, setTokens]);

  useEffect(() => {
    reloadTokens();
  }, [reloadTokens]);

  useEffect(() => {
    if (!mapId) return;
    const i = setInterval(() => {
      reloadTokens();
    }, 1000);
    return () => clearInterval(i);
  }, [mapId, reloadTokens]);

  useEffect(() => {
    if (addTokenMode) {
      setAddTypeModal(true);
      setPendingAddType(null);
      setAddTokenReady(false);
    }
  }, [addTokenMode]);

  useEffect(() => {
    if (!isMapOwnerOrFounder) return;
    if (!openPlayerTokenId) return;
    const idx = (tokens || []).findIndex(t => String(t.id) === String(openPlayerTokenId) && (t.type === 'player' || t.type === 'npc'));
    if (idx === -1) {
      if (typeof onPlayerTokenModalHandled === 'function') onPlayerTokenModalHandled();
      return;
    }
    setModalTokenIdx(idx);
    setModalData({ ...tokens[idx] });
    if (typeof onPlayerTokenModalHandled === 'function') onPlayerTokenModalHandled();
  }, [openPlayerTokenId, tokens, isMapOwnerOrFounder, onPlayerTokenModalHandled]);

  useEffect(() => {
    if (!tokens) return;

    const sightMarkers = tokens
      .filter(t => t.type === "player")
      .map(t => ({ x: t.x, y: t.y }));

    const lightMarkers = tokens
      .filter(t => t.emits_light === true || t.emits_light === 1)
      .map(t => ({
        id: t.id,
        type: t.type,
        light_type: t.light_type,
        x: t.x,
        y: t.y,
        reach: Number(t.reach) || 0,
        reach_units: t.reach_units || "metres",
        intensity: typeof t.intensity === "number" ? t.intensity : 1,
        colour: t.colour
      }));

    if (typeof onSightLightMarkersChange === "function") {
      onSightLightMarkersChange({ sightMarkers, lightMarkers });
    }
  }, [tokens, onSightLightMarkersChange]);

  function selectType(type) {
    setPendingAddType(type);
    setAddTypeModal(false);
    setAddTokenReady(true);
  }

  async function handleMapClick(coord) {
    if (!mapId) {
      alert("No valid map loaded, cannot add token.");
      setAddTokenReady(false);
      if (onFinishAddToken) onFinishAddToken();
      return;
    }

    if ((pendingAddType === "light" || pendingAddType === "npc") && !isMapOwnerOrFounder) {
      alert(pendingAddType === "npc" ? "Only the GM can add NPC tokens." : "Only the GM can add Light Source tokens.");
      setAddTokenReady(false);
      if (onFinishAddToken) onFinishAddToken();
      return;
    }

    let newToken;

    if (pendingAddType === "light") {
      newToken = {
        map_id: mapId,
        user_id: user?.id,
        label: "Light Source",
        icon_url: "",
        type: "light",
        x: coord.x,
        y: coord.y,
        intensity: 1.0,
        reach: 1,
        colour: "#ffe072",
        light_type: "flame",
        emits_light: 1,
        flicker: 1,
        visible_to_all: 1
      };
    } else if (pendingAddType === "npc") {
      newToken = {
        map_id: mapId,
        user_id: user?.id,
        controlled_user_id: null,
        label: "NPC",
        icon_url: "",
        type: "npc",
        x: coord.x,
        y: coord.y,
        intensity: 1.0,
        reach: 0,
        colour: "#22c55e",
        light_type: "none",
        emits_light: 0,
        flicker: 0,
        visible_to_all: 1
      };
    } else {
      newToken = {
        map_id: mapId,
        user_id: user?.id,
        label: "Player",
        icon_url: "",
        type: "player",
        x: coord.x,
        y: coord.y,
        intensity: 1.0,
        reach: 0,
        colour: "#3b82f6",
        light_type: "none",
        emits_light: 0,
        flicker: 0,
        visible_to_all: 1
      };
    }

    let saved = null;

    try {
      const res = await fetch(TOKENS_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newToken)
      });
      if (!res.ok) throw new Error("Failed to save marker: " + res.status);
      saved = await res.json();
    } catch (e) {
      alert("Error saving marker: " + e.message);
      setAddTokenReady(false);
      if (onFinishAddToken) onFinishAddToken();
      return;
    }

    reloadTokens();
    setAddTokenReady(false);

    setTimeout(() => {
      setModalTokenIdx(tokens.length);
      setModalData(saved);
    }, 300);

    if (onFinishAddToken) onFinishAddToken();
  }

  async function handleTokenDrag(idx, e) {
    if (!mapId) return;
    const { lat, lng } = e.target.getLatLng();
    const token = tokens[idx];
    if (!token) return;
    const updated = { ...token, x: lng, y: lat, map_id: mapId };
    try {
      await fetch(`${TOKENS_API_URL}/${token.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updated)
      });
      reloadTokens();
    } catch (e) {}
  }

  function openEditModal(idx) {
    if (idx == null) return;
    if (!tokens[idx]) return;
    setModalTokenIdx(idx);
    setModalData({ ...tokens[idx] });
  }

  function closeModal() {
    setModalTokenIdx(null);
    setModalData(null);
    setUploadingIcon(false);
  }

  async function handleModalSave() {
    const token = modalData;
    if (!token || !token.id) {
      closeModal();
      return;
    }

    const tokenWithMapId = { ...token, map_id: mapId };

    try {
      await fetch(`${TOKENS_API_URL}/${token.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(tokenWithMapId)
      });
      reloadTokens();
    } catch (e) {}

    closeModal();
  }

  async function handleModalDelete() {
    const token = tokens[modalTokenIdx];
    if (!token || !token.id) {
      closeModal();
      return;
    }

    try {
      await fetch(`${TOKENS_API_URL}/${token.id}`, { method: "DELETE" });
      reloadTokens();
    } catch (e) {}

    closeModal();
  }

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingIcon(true);
    const url = URL.createObjectURL(file);
    setModalData(md => ({ ...md, icon_url: url }));
    setUploadingIcon(false);
  }

  const [tokenPx, setTokenPx] = useState(36);

  const updateTokenSize = useCallback(() => {
    if (!map || !gridConfig || !gridConfig.cell_size) return;
    const px = imgPxToScreenPx(map, gridConfig.cell_size);
    setTokenPx(Math.max(8, px));
  }, [map, gridConfig?.cell_size]);

  useEffect(() => {
    updateTokenSize();
  }, [updateTokenSize, imageSize]);

  useEffect(() => {
    if (!map) return;
    const handler = () => updateTokenSize();
    map.on('zoom zoomend move resize', handler);
    return () => map.off('zoom zoomend move resize', handler);
  }, [map, updateTokenSize]);

  const lightPx = useMemo(() => Math.max(6, tokenPx / 3), [tokenPx]);

  const lightTokens = tokens.filter(t => t.type === "light");

  return (
    <>
      {addTypeModal && (
        <div style={{
          position: "fixed",
          left: "50%", top: "36%",
          transform: "translate(-50%, -50%)",
          background: "#23242a",
          padding: 26,
          borderRadius: 15,
          color: "#fff",
          zIndex: 2700,
          boxShadow: "0 4px 22px #000b",
          minWidth: 340
        }}>
          <div style={{ marginBottom: 13, fontWeight: 700, fontSize: 19 }}>
            Add Marker
          </div>
          <div style={{ marginBottom: 22 }}>
            <button
              style={{ margin: "6px 0", width: "100%", background: "#3b82f6", color: "#fff", border: 'none', borderRadius: 6, padding: "8px 0", fontWeight: 700, fontSize: 17, cursor: "pointer" }}
              onClick={() => selectType("player")}
            >Player Token</button>
            <button
              style={{ margin: "6px 0", width: "100%", background: "#22c55e", color: "#fff", border: 'none', borderRadius: 6, padding: "8px 0", fontWeight: 700, fontSize: 17, cursor: "pointer" }}
              onClick={() => selectType("npc")}
              disabled={!isMapOwnerOrFounder}
              title={!isMapOwnerOrFounder ? "Only the GM can add NPC tokens." : ""}
            >NPC Token</button>
            <button
              style={{ margin: "6px 0", width: "100%", background: "#ffe072", color: "#222", border: 'none', borderRadius: 6, padding: "8px 0", fontWeight: 700, fontSize: 17, cursor: "pointer" }}
              onClick={() => selectType("light")}
              disabled={!isMapOwnerOrFounder}
              title={!isMapOwnerOrFounder ? "Only the GM can add Light Source tokens." : ""}
            >Light Source</button>
          </div>
          <div style={{ fontSize: 13, color: "#aaa" }}>
            Only the GM can add NPC and Light Source tokens.<br />
            Player and NPC tokens are rendered by PIXI, not Leaflet.
          </div>
          <button
            style={{ background: "none", color: "#fff", border: "1px solid #aaa", borderRadius: 7, padding: "4px 12px", fontWeight: 700, fontSize: 15, cursor: "pointer", marginTop: 14 }}
            onClick={() => setAddTypeModal(false)}
          >Cancel</button>
        </div>
      )}

      {addTokenReady && (
        <ClickHandler onClick={handleMapClick} enabled={true} />
      )}

      {isMapOwnerOrFounder && lightTokens.map((token) => {
        const idx = tokens.indexOf(token);
        if (idx === -1) return null;
        return (
          <TokenMarker
            key={token.id}
            token={token}
            sizePx={lightPx}
            isLight={true}
            onDragEnd={(e) => handleTokenDrag(idx, e)}
            onClick={() => openEditModal(idx)}
          />
        );
      })}

      {modalTokenIdx !== null && modalData && (
        <>
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "transparent",
              zIndex: 22599,
              cursor: "default",
            }}
          />
          <div style={{
            position: "fixed",
            left: "50%",
            top: "38%",
            transform: "translate(-50%, -50%)",
            background: "#222225",
            padding: 30,
            borderRadius: 14,
            color: "#fff",
            zIndex: 22600,
            boxShadow: "0 4px 34px #000d",
            minWidth: 350
          }}>
            <div style={{ marginBottom: 12, fontWeight: 700, fontSize: 17 }}>
              {modalData.type === 'light' ? "Edit Light Source" : modalData.type === 'npc' ? "Edit NPC Token" : "Edit Player Token"}
            </div>
            <div style={{ marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 7 }}>
              <label>
                <span style={{ fontSize: 15 }}>Label</span>
                <input
                  type="text"
                  value={modalData.label}
                  onChange={e => setModalData(md => ({ ...md, label: e.target.value }))}
                  style={{ width: "100%", padding: 8, borderRadius: 7, border: "1px solid #888", background: "#19191e", color: "#fff", fontSize: 15 }}
                />
              </label>

              {(modalData.type === 'player' || modalData.type === 'npc') && (
                <>
                  <label>
                    <span style={{ fontSize: 15 }}>Colour</span>
                    <input
                      type="color"
                      value={modalData.colour || (modalData.type === 'npc' ? '#22c55e' : '#3b82f6')}
                      onChange={e => setModalData(md => ({ ...md, colour: e.target.value }))}
                      style={{ marginLeft: 13, width: 38, height: 28, verticalAlign: 'middle' }}
                    />
                    <span style={{ marginLeft: 8, fontSize: 14, color: modalData.type === 'npc' ? "#86efac" : "#9cc6ff" }}>
                      {(modalData.colour || (modalData.type === 'npc' ? '#22c55e' : '#3b82f6')).toUpperCase()}
                    </span>
                  </label>
                  <label>
                    <span style={{ fontSize: 15 }}>Visible To All</span>
                    <input
                      type="checkbox"
                      checked={!!modalData.visible_to_all}
                      onChange={e => setModalData(md => ({ ...md, visible_to_all: e.target.checked ? 1 : 0 }))}
                      style={{ marginLeft: 10 }}
                    />
                  </label>
                </>
              )}

              {modalData.type === 'light' && (
                <>
                  <label>
                    <span style={{ fontSize: 15 }}>Light On</span>
                    <input
                      type="checkbox"
                      checked={!!modalData.emits_light}
                      onChange={e => setModalData(md => ({ ...md, emits_light: e.target.checked ? 1 : 0 }))}
                      style={{ marginLeft: 10 }}
                    />
                  </label>
                  <label>
                    <span style={{ fontSize: 15 }}>Range (meters)</span>
                    <input
                      type="range"
                      min={1}
                      max={20}
                      value={modalData.reach}
                      onChange={e => setModalData(md => ({ ...md, reach: Number(e.target.value) }))}
                      style={{ width: '100%' }}
                    />
                    <div style={{ textAlign: 'right', fontSize: 14 }}>{modalData.reach} m</div>
                  </label>
                  <label>
                    <span style={{ fontSize: 15 }}>Intensity</span>
                    <input
                      type="number"
                      step="0.1"
                      value={modalData.intensity}
                      onChange={e => setModalData(md => ({ ...md, intensity: Number(e.target.value) }))}
                      style={{ marginLeft: 6, width: 60 }}
                      min={0.1}
                      max={3}
                    />
                  </label>
                  <label>
                    <span style={{ fontSize: 15 }}>Colour</span>
                    <input
                      type="color"
                      value={modalData.colour}
                      onChange={e => setModalData(md => ({ ...md, colour: e.target.value }))}
                      style={{ marginLeft: 13, width: 38, height: 28, verticalAlign: 'middle' }}
                    />
                    <span style={{ marginLeft: 8, fontSize: 14, color: "#ffe670" }}>{modalData.colour?.toUpperCase()}</span>
                  </label>
                  <label>
                    <span style={{ fontSize: 15 }}>Effect</span>
                    <select
                      value={modalData.flicker ? "flicker" : "solid"}
                      onChange={e => setModalData(md => ({ ...md, flicker: e.target.value === "flicker" ? 1 : 0 }))}
                      style={{ marginLeft: 8, fontSize: 15 }}
                    >
                      <option value="solid">Solid Light</option>
                      <option value="flicker">Flickering</option>
                    </select>
                  </label>
                  <label>
                    <span style={{ fontSize: 15 }}>Icon/Animation</span>
                    <input
                      type="file"
                      accept="image/*,video/*"
                      onChange={handleFileChange}
                      style={{ marginLeft: 10, fontSize: 13 }}
                    />
                    {modalData.icon_url && (isImageFile(modalData.icon_url) || isVideoFile(modalData.icon_url)) && (
                      <span style={{ marginLeft: 10 }}>
                        {isImageFile(modalData.icon_url) && (
                          <img src={modalData.icon_url} alt="icon" style={{ width: 32, height: 32, borderRadius: 5, verticalAlign: 'middle' }} />
                        )}
                        {isVideoFile(modalData.icon_url) && (
                          <video src={modalData.icon_url} style={{ width: 32, height: 32, borderRadius: 5, verticalAlign: 'middle' }} autoPlay loop muted />
                        )}
                      </span>
                    )}
                  </label>
                  <label>
                    <span style={{ fontSize: 15 }}>Visible To All</span>
                    <input
                      type="checkbox"
                      checked={!!modalData.visible_to_all}
                      onChange={e => setModalData(md => ({ ...md, visible_to_all: e.target.checked ? 1 : 0 }))}
                      style={{ marginLeft: 10 }}
                    />
                  </label>
                </>
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 13, marginTop: 14 }}>
              <button
                style={{
                  background: "#4cc153", color: "#fff", border: "none", borderRadius: 7,
                  padding: "5px 18px", fontWeight: 700, fontSize: 15, cursor: "pointer"
                }}
                onClick={handleModalSave}
              >Save</button>
              <button
                style={{
                  background: "#f44c2b", color: "#fff", border: "none", borderRadius: 7,
                  padding: "5px 14px", fontWeight: 700, fontSize: 15, cursor: "pointer"
                }}
                onClick={handleModalDelete}
              >Delete</button>
              <button
                style={{
                  background: "none", border: "1px solid #aaa", borderRadius: 7,
                  color: "#fff", padding: "5px 18px", fontWeight: 600, fontSize: 15, cursor: "pointer"
                }}
                onClick={closeModal}
              >Cancel</button>
            </div>
          </div>
        </>
      )}
    </>
  );
}